import { useErrorReporter } from '@/composables/useErrorReporter'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import {
	onMounted,
	ref
} from 'vue'

interface useMapParams {
	mapCenter?: google.maps.LatLngLiteral
	address?: string
	elementId: string
	pin?: boolean
	draggable?: boolean
	roadMap?: boolean
	zoom?: number
	showZoomControl?: boolean
}

export const useMap = async ({
	mapCenter, address, elementId, pin, draggable, roadMap, zoom, showZoomControl
}: useMapParams) => {
	const pinCoords = ref<google.maps.LatLngLiteral | undefined>(mapCenter)
	const { bugsnagReport } = useErrorReporter()

	onMounted(() => {
		setOptions({
			key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
			version: 'quarterly'
		})
		let map: google.maps.Map
		importLibrary('maps').then(async () => {
			if (address) {
				const { Geocoder } = await google.maps.importLibrary('geocoding') as google.maps.GeocodingLibrary
				const geocoder = new Geocoder()
				await geocoder.geocode({
					address
				}, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
					if (status === google.maps.GeocoderStatus.OK) {
						mapCenter = { lat: results?.[0]?.geometry?.location?.lat() || 0,
							lng: results?.[0]?.geometry?.location?.lng() || 0 }
						pinCoords.value = mapCenter
					} else {
						bugsnagReport({
							error: new Error(`Geocode was not successful for the following reason: ' + ${status}`),
							showToast: false
						})
					}
				})
			}
			const {
				Map, MapTypeId
			} = await google.maps.importLibrary('maps') as google.maps.MapsLibrary
			map = new Map(document.getElementById(elementId) as HTMLElement, {
				center: mapCenter,
				zoom,
				mapId: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
				mapTypeId: roadMap ? MapTypeId.ROADMAP : MapTypeId.HYBRID,
				mapTypeControl: true,
				mapTypeControlOptions: {
					style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
				},
				zoomControl: showZoomControl,
				controlSize: showZoomControl ? null : 2
			})
			if (pin) {
				const { PinElement } = await google?.maps?.importLibrary('marker') as google.maps.MarkerLibrary
				const { AdvancedMarkerElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary
				const mainPin = new PinElement({
					glyphColor: 'white'
				})
				const marker = new AdvancedMarkerElement({
					map,
					position: mapCenter,
					gmpDraggable: draggable,
					content: mainPin.element
				})
				marker.addListener('dragend', (event: { latLng: google.maps.LatLng }) => {
					pinCoords.value = { lat: event.latLng.lat(),
						lng: event.latLng.lng() }
				})
			}
		}).catch(error => {
			bugsnagReport({
				error: new Error(error.message),
				showToast: false
			})
		})
	})
	return { pinCoords }
}
